import { useEffect, useMemo, useState } from "react";
import type { BoardCollapse } from "./boardCollapse";

/**
 * How long a dragged card rests over a collapsed column before it opens:
 * just enough that sweeping across a strip doesn't pop it open.
 */
export const BOARD_HOVER_EXPAND_DELAY_MS = 150;

interface BoardHover {
    columnId: string | null;
    isExpanded: boolean;
}

const NO_HOVER: BoardHover = { columnId: null, isExpanded: false };

const withExpandedColumn = (
    collapse: BoardCollapse,
    columnId: string | null,
): BoardCollapse =>
    columnId === null || !collapse.columnIds.has(columnId)
        ? collapse
        : {
              ...collapse,
              columnIds: new Set(
                  Array.from(collapse.columnIds).filter(
                      (id) => id !== columnId,
                  ),
              ),
          };

interface UseBoardHoverExpandArgs {
    collapse: BoardCollapse;
    /** The column a pointer drag targets; null with no pointer drag. */
    hoveredColumnId: string | null;
}

/**
 * `collapse` with the collapsed column a pointer drag has rested over for
 * `BOARD_HOVER_EXPAND_DELAY_MS` shown expanded, so the card can be placed
 * among its cards. The column stays the target while the pointer is over any
 * of its cells, so it doesn't fold back under the pointer; it folds once the
 * drag targets another column or ends. Never reported to the surface.
 */
export const useBoardHoverExpand = ({
    collapse,
    hoveredColumnId,
}: UseBoardHoverExpandArgs) => {
    const [hover, setHover] = useState(NO_HOVER);
    if (hover.columnId !== hoveredColumnId) {
        setHover({ columnId: hoveredColumnId, isExpanded: false });
    }
    const pendingColumnId =
        hoveredColumnId !== null && collapse.columnIds.has(hoveredColumnId)
            ? hoveredColumnId
            : null;
    useEffect(() => {
        if (pendingColumnId === null) {
            return undefined;
        }
        const timeout = window.setTimeout(
            () =>
                setHover((current) =>
                    current.columnId === pendingColumnId
                        ? { ...current, isExpanded: true }
                        : current,
                ),
            BOARD_HOVER_EXPAND_DELAY_MS,
        );
        return () => window.clearTimeout(timeout);
    }, [pendingColumnId]);
    const expandedColumnId =
        hover.columnId === hoveredColumnId && hover.isExpanded
            ? hoveredColumnId
            : null;
    return useMemo(
        () => withExpandedColumn(collapse, expandedColumnId),
        [collapse, expandedColumnId],
    );
};
